import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from "react-router-dom";
import AuthLayout from "../../components/auth/AuthLayout";
import AuthForm from "../../components/auth/AuthForm";
import FormField from "../../components/auth/FormField";
import PasswordInput from "../../components/auth/PasswordInput";
import Checkbox from "../../components/form/input/Checkbox";
import Button from "../../components/ui/button/Button";
import { supabase } from "../../integrations/supabase/client";
import { userStateService } from "../../services/UserStateService";

export default function SignIn() {
  const location = useLocation();
  const navigate = useNavigate();
  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  
  // Get verification message from location state if coming from signup
  const { verificationEmail, message, status } = location.state || {};
  const [notificationMessage, setNotificationMessage] = useState<{ text: string, type: "success" | "error" | "info" } | null>(
    message ? { text: message, type: status || "info" } : null
  );

  useEffect(() => {
    // Check if user is already logged in
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        console.log('User is already logged in');
        navigate('/');
      }
    });
    
    // If verification email is provided, pre-fill the email field
    if (verificationEmail && emailInputRef.current) {
      emailInputRef.current.value = verificationEmail;
    }
  }, [navigate, verificationEmail]);
  
  const [isChecked, setIsChecked] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null); // Clear any previous errors
    setNotificationMessage(null); // Clear any notification
    
    const email = emailInputRef.current?.value || '';
    const password = passwordInputRef.current?.value || '';

    if (!email || !password) {
      setErrorMessage('Please fill in all fields');
      return;
    }

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        // Check for email verification error specifically
        if (error.message.includes("Email not confirmed")) {
          setErrorMessage("Please verify your email before signing in. Check your inbox for a verification link.");
        } else {
          setErrorMessage(error.message);
        }
      } else {
        // Set user info in UserStateService
        userStateService.setUserInfo(
          data.user?.id || '',
          data.user?.user_metadata?.name || 'User',
          data.user?.email || ''
        );
        navigate('/');
      }
    } catch (err) {
      setErrorMessage('An unexpected error occurred. Please try again.');
    }
  };

  return (
    <AuthLayout
      title="Sign In"
      subtitle="Enter your email and password to sign in"
      metaTitle="Sign In | PDF ConvertOne"
      metaDescription="Sign in to your PDF ConvertOne account"
    >
      <AuthForm
        onSubmit={handleSignIn}
        errorMessage={errorMessage}
        bottomContent={
          <p className="text-sm font-normal text-center text-gray-700 dark:text-gray-400 sm:text-start">
            Don't have an account? {""}
            <Link
              to="/signup"
              className="text-brand-500 hover:text-brand-600 dark:text-brand-400"
            >
              Sign Up for Free
            </Link>
            {""} (no credit card required!)
          </p>
        }
      >
        {/* Notification message */}
        {notificationMessage && (
          <div className={`p-4 mb-6 text-sm rounded-lg ${
            notificationMessage.type === 'success' 
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
              : notificationMessage.type === 'error'
              ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
              : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
          }`}>
            {notificationMessage.text}
          </div>
        )}
        
        <FormField
          label="Email"
          placeholder="alex.smith@example.com"
          ref={emailInputRef}
          required
        />
        
        <PasswordInput
          label="Password"
          placeholder="Enter your password"
          ref={passwordInputRef}
          required
        />
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Checkbox checked={isChecked} onChange={setIsChecked} />
            <span className="block font-normal text-gray-700 text-theme-sm dark:text-gray-400">
              Keep me logged in
            </span>
          </div>
          <Link
            to="/forgot-password"
            className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
          >
            Forgot password?
          </Link>
        </div>
        
        <div>
          <Button className="w-full" size="sm">
            Sign in
          </Button>
        </div>
      </AuthForm>
    </AuthLayout>
  );
}
